import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  ChevronLeft, Send, CheckCircle, RotateCcw,
  Loader, AlertTriangle, Shield, User, Clock
} from 'lucide-react';
import toast from 'react-hot-toast';

const PRIORITY_CONFIG = {
  low:    { label: 'Low',    color: 'text-gray-400',   bg: 'bg-gray-500/10 border-gray-500/20' },
  normal: { label: 'Normal', color: 'text-blue-400',   bg: 'bg-blue-500/10 border-blue-500/20' },
  high:   { label: 'High',   color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20' },
  urgent: { label: 'Urgent', color: 'text-red-400',    bg: 'bg-red-500/10 border-red-500/20' },
};

const TicketDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/tickets/${id}`).then(({ data }) => {
      setTicket(data.ticket);
      setReplies(data.replies);
    }).catch(() => toast.error('Ticket not found')).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replies]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/tickets/${id}/reply`, { message: message.trim() });
      setReplies((prev) => [...prev, data.reply]);
      setMessage('');
      toast.success('Reply sent');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const closeTicket = async () => {
    if (!window.confirm('Close this ticket?')) return;
    setClosing(true);
    try {
      await api.patch(`/tickets/${id}/close`);
      setTicket((prev) => ({ ...prev, status: 'closed' }));
      toast.success('Ticket closed');
    } catch {
      toast.error('Failed to close ticket');
    } finally {
      setClosing(false);
    }
  };

  const reopenTicket = async () => {
    try {
      await api.patch(`/tickets/${id}/reopen`);
      setTicket((prev) => ({ ...prev, status: 'open' }));
      toast.success('Ticket reopened');
    } catch {
      toast.error('Failed to reopen ticket');
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <Loader className="w-8 h-8 text-green-400 animate-spin" />
    </div>
  );

  if (!ticket) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="text-center">
        <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-white font-bold">Ticket not found</p>
        <Link to="/tickets" className="text-green-400 text-sm mt-2 block">← Back to tickets</Link>
      </div>
    </div>
  );

  const priority = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.normal;
  const isClosed = ticket.status === 'closed';

  return (
    <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          <Link to="/tickets" className="inline-flex items-center gap-1.5 text-gray-400 hover:text-white text-sm mb-4 transition-colors">
            <ChevronLeft className="w-4 h-4" /> All tickets
          </Link>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-white mb-2">{ticket.subject}</h1>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-gray-500">#{ticket.id.slice(0, 8).toUpperCase()}</span>
                <span className={`px-2 py-0.5 rounded-full border font-medium ${priority.bg} ${priority.color}`}>
                  {priority.label}
                </span>
                <span className="text-gray-500 capitalize">{ticket.category}</span>
                <span className="text-gray-500">·</span>
                <span className="text-gray-500">{new Date(ticket.created_at).toLocaleString()}</span>
                {ticket.server_name && (
                  <span className="bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full">🖥 {ticket.server_name}</span>
                )}
              </div>
            </div>

            {/* Status + actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {isClosed ? (
                <>
                  <div className="flex items-center gap-1.5 text-gray-400 text-sm">
                    <CheckCircle className="w-4 h-4" /> Closed
                  </div>
                  <button
                    onClick={reopenTicket}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 text-sm rounded-xl transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Reopen
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-1.5 text-green-400 text-sm">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" /> Open
                  </div>
                  <button
                    onClick={closeTicket}
                    disabled={closing}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 text-sm rounded-xl transition-colors disabled:opacity-50"
                  >
                    {closing ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                    Close ticket
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Thread */}
        <div className="space-y-4 mb-6">
          {replies.map((reply, i) => {
            const isStaff = reply.is_staff;
            const isMe = reply.user_id === user?.id;
            return (
              <div key={reply.id} className={`flex gap-3 ${isStaff ? 'flex-row-reverse' : ''}`}>
                {/* Avatar */}
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isStaff ? 'bg-green-500/20' : 'bg-gray-800'
                }`}>
                  {isStaff
                    ? <Shield className="w-4 h-4 text-green-400" />
                    : <User className="w-4 h-4 text-gray-400" />
                  }
                </div>

                {/* Bubble */}
                <div className={`flex-1 max-w-[85%] ${isStaff ? 'items-end' : 'items-start'} flex flex-col`}>
                  <div className={`flex items-center gap-2 mb-1.5 ${isStaff ? 'flex-row-reverse' : ''}`}>
                    <span className="text-sm font-semibold text-white">
                      {isStaff ? 'PlarixMC Support' : `${reply.first_name} ${reply.last_name}`}
                    </span>
                    {isStaff && (
                      <span className="text-xs bg-green-500/10 text-green-400 border border-green-500/20 px-2 py-0.5 rounded-full">Staff</span>
                    )}
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(reply.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    isStaff
                      ? 'bg-green-500/10 border border-green-500/20 text-gray-200 rounded-tr-sm'
                      : 'bg-gray-900 border border-gray-800 text-gray-200 rounded-tl-sm'
                  }`}>
                    {reply.message}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Reply box */}
        {isClosed ? (
          <div className="bg-gray-900 border border-gray-800 border-dashed rounded-2xl p-5 text-center">
            <CheckCircle className="w-6 h-6 text-gray-500 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">This ticket is closed.</p>
            <button onClick={reopenTicket} className="text-green-400 hover:text-green-300 text-sm mt-1 transition-colors">
              Reopen to send a message →
            </button>
          </div>
        ) : (
          <form onSubmit={sendReply} className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
            <label className="block text-sm font-medium text-gray-300 mb-2">Your reply</label>
            <textarea
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) sendReply(e); }}
              placeholder="Type your message... (Ctrl+Enter to send)"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors resize-none text-sm mb-3"
            />
            <div className="flex items-center justify-between">
              <span className="text-gray-600 text-xs">Ctrl+Enter to send quickly</span>
              <button
                type="submit"
                disabled={sending || !message.trim()}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-400 disabled:opacity-40 disabled:cursor-not-allowed text-gray-950 font-bold px-5 py-2.5 rounded-xl text-sm transition-colors"
              >
                {sending ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Reply
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TicketDetailPage;
